<script>
    import { onMount } from "svelte";

    import {
        CardLayout,
        NotificationModal,
        PageLoading,
        SvelteTable,
        TableToggle,
    } from "../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        getFormatDateAndTime,
        getText,
        sortEnumAlphabetically,
    } from "../../../Utils/utilFunctions.svelte";

    import { insuranceFund } from "../../../stores";

    import { cacheInsuranceFunds } from "../../../Utils/utilCache.svelte";

    import { AustralianStates } from "../../../Utils/utilEnums.svelte";

    export let accountId = "";

    let isGettingInsuranceFund = false,
        isGettingAccountActiveInsuranceFund = false,
        accountActiveInsuranceFund = [],
        groupedInsuranceFund = [];

    let selectedFund = {},
        isUpdatingInsuranceFund = false;

    let isDeactiveFundModalOpen = false;

    onMount(async () => {
        if (!$insuranceFund.length) await loadInsuranceFund();
        else groupedInsuranceFund = _groupInsuranceFund($insuranceFund);

        await loadAccountActiveInsuranceFund();
    });

    const columns = [
        {
            key: "code",
            title: "Group Name",
            value: (v) => (v.nameHidden ? "" : getText(v.key)),
            headerClass: "text-left pl-4 text-[14px] w-1/8",
            class: "text-left pl-4 text-[14px] cursor-auto w-1/8",
        },
        {
            key: "fundNames",
            title: "Funds",
            value: (v) => _getFundsName(v.value),
            headerClass: "text-left pl-4 text-[14px] w-3/4",
            class: "text-left pl-4 text-[14px] cursor-auto w-3/4",
        },
        {
            key: "lastUpdated",
            title: "Last Updated",
            value: (v) => getFormatDateAndTime(v?.updatedDate),
            headerClass: "text-left pl-4 text-[14px]",
            class: "text-left pl-4 text-[14px] cursor-auto",
        },
        {
            key: "active",
            title: "Enable Claiming",
            headerClass: "text-left pl-4 text-[14px] rounded-tr-xl w-[2%]",
            class: "text-left pl-4 text-[14px] cursor-auto",
            renderComponent: {
                component: TableToggle,
                props: {
                    valueKey: "isActive",
                    clickFunction: async (data) => {
                        selectedFund = { ...data };

                        if (data.isActive) isDeactiveFundModalOpen = true;
                        else updateFund();
                    },
                },
            },
        },
    ];

    const loadInsuranceFund = async () => {
        isGettingInsuranceFund = true;

        await cacheInsuranceFunds();

        groupedInsuranceFund = _groupInsuranceFund($insuranceFund);

        isGettingInsuranceFund = false;
    };

    const loadAccountActiveInsuranceFund = async () => {
        isGettingAccountActiveInsuranceFund = true;

        await apiMethods.getMethod
            .getAccountActiveInsuranceFund(accountId)
            .then((res) => (accountActiveInsuranceFund = [...res]))
            .then(async () => await initFundActivity());

        isGettingAccountActiveInsuranceFund = false;
    };

    const initFundActivity = () => {
        groupedInsuranceFund = groupedInsuranceFund.map((fund) => {
            if (!accountActiveInsuranceFund.length)
                return { ...fund, isActive: false };

            const { includeCodes } = fund;

            const filteredFunds = accountActiveInsuranceFund.filter(
                ({ code }) => includeCodes.includes(code),
            );

            if (!filteredFunds.length) return { ...fund, isActive: false };

            return {
                ...fund,
                isActive: !filteredFunds.some((fd) => !fd.active),
                updatedDate: filteredFunds[0]?.updatedDate,
            };
        });
    };

    const updateFund = async () => {
        isUpdatingInsuranceFund = true;

        const eclipseCodeArr = [];

        selectedFund.value.map((fund) => {
            if (!eclipseCodeArr.includes(fund.eclipseCode))
                eclipseCodeArr.push(fund.eclipseCode);
        });

        while (eclipseCodeArr.length) {
            if (
                !accountActiveInsuranceFund.length ||
                !accountActiveInsuranceFund.some(
                    (fund) => fund.code == eclipseCodeArr[0],
                )
            ) {
                await apiMethods.postMethod.addInsuranceFundToAccount({
                    accountId,
                    code: eclipseCodeArr[0],
                    active: true,
                });
            } else
                await apiMethods.putMethod.updateAccountInsuranceFund({
                    accountId,
                    code: eclipseCodeArr[0],
                    active: !selectedFund.isActive,
                    id: accountActiveInsuranceFund.filter(
                        (fund) => fund.code == eclipseCodeArr[0],
                    )[0].id,
                });

            eclipseCodeArr.shift();
        }

        await loadAccountActiveInsuranceFund();

        closeDeactiveFundModal();

        isUpdatingInsuranceFund = false;
    };

    const closeDeactiveFundModal = () => {
        isDeactiveFundModalOpen = false;
        selectedFund = {};
    };

    const handleClickCell = (
        { target: { className, classList } },
        rowData,
        key,
    ) => {
        if (key == "active" && className.includes("checkbox")) {
            selectedFund = { ...rowData };
            updateFund();
        }
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {};

    // HELPER FUNCTIONS
    const _groupInsuranceFund = (insuranceFund) => {
        let res = []; // [{key: groupName1, value: [...], includeCodes: [...]}, {key: groupName2, value: [...], includeCodes: [...]}]

        insuranceFund.map((fund) => {
            const { groupName, eclipseCode } = fund;

            if (res.some((groupFund) => groupFund.key == groupName))
                res.map((groupFund) => {
                    if (groupFund.key == groupName) {
                        groupFund.value.push({ ...fund });
                        if (!groupFund.includeCodes.includes(eclipseCode))
                            groupFund.includeCodes.push(eclipseCode);
                    }
                });
            else
                res.push({
                    key: groupName,
                    value: [{ ...fund }],
                    includeCodes: [eclipseCode],
                });
        });

        res = res.sort((a, b) => sortEnumAlphabetically(a.key, b.key));

        const states = AustralianStates.map((state) => state.key);

        const wcFunds = [],
            tacFunds = [];

        states.map((state, i) => {
            wcFunds.push({
                key: "WorkCover",
                value: [
                    {
                        name: `WorkCover - ${state}`,
                        eclipseCode: `WorkCover${state}`,
                    },
                ],
                includeCodes: [`WorkCover${state}`],
                nameHidden: i > 0,
            });
            tacFunds.push({
                key: "TrafficAccident",
                value: [
                    {
                        name: `TrafficAccident - ${state}`,
                        eclipseCode: `TrafficAccident${state}`,
                    },
                ],
                includeCodes: [`TrafficAccident${state}`],
                nameHidden: i > 0,
            });
        });

        const defenceFunds = [
            {
                key: "Defence",
                value: [
                    {
                        name: "Australian Defence Force",
                        eclipseCode: "Defence",
                    },
                ],
                includeCodes: ["Defence"],
            },
        ];

        const thirdPartyInsurerFunds = [
            {
                key: "ThirdPartyInsurer",
                value: [
                    {
                        name: "3rd Party Insurers",
                        eclipseCode: "ThirdPartyInsurer",
                    },
                ],
                includeCodes: ["ThirdPartyInsurer"],
            },
        ];

        return [
            ...res,
            ...wcFunds,
            ...tacFunds,
            ...defenceFunds,
            ...thirdPartyInsurerFunds,
        ];
    };

    const _getFundsName = (arr) => {
        if (!arr.length) return "";

        const res = [];

        arr.map((fund) => res.push(fund.name));

        return res.join(", ");
    };
</script>

<CardLayout title="Active Insurance Funds">
    {#if isGettingInsuranceFund}
        <PageLoading />
    {:else if groupedInsuranceFund.length}
        <div class="flex flex-1 overflow-y-auto">
            <SvelteTable
                {columns}
                rows={groupedInsuranceFund}
                clickCellFunc={({ detail: { event, row, key } }) => {
                    handleClickCell(event, row, key);
                }}
                clickColFunc={({ detail: { event, row, key } }) => {
                    handleClickCol(event, row, key);
                }}
            />
        </div>
    {:else}
        <div class="grid flex-1 h-full card bg-white rounded-box">
            <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
                <p>No Insurance Funds</p>
            </div>
        </div>
    {/if}
</CardLayout>

<NotificationModal
    isModalOpen={isDeactiveFundModalOpen}
    title="Notice"
    closeModal={closeDeactiveFundModal}
    isConfirming={isUpdatingInsuranceFund}
    submitText={isUpdatingInsuranceFund ? "Confirming" : "Confirm"}
    cancelText="Cancel"
    confirmAction={updateFund}
    closeModalFunction={closeDeactiveFundModal}
>
    Deactive this fund and disable claims for the account?
</NotificationModal>
